import { useState, useCallback, useRef } from 'react';
import {
    processInvoiceData,
    buildSearchParams,
    buildSummaryParams,
    mapIssuanceSummaryToUi,
    ISSUANCE_PAGE_LIMIT
} from '../utils/invoiceUtils';
import { fetchItemListCached, getItemListCached } from '../../../../utils/cache/itemCache';
import { fetchVendorListCached, getVendorListCached } from '../../../../utils/cache/vendorCache';
import { fetchBuyerListCached, getBuyerListCached } from '../../../../utils/cache/buyerCache';
import { fetchManagerListCached, getManagerListCached } from '../../../../utils/cache/managerCache';
import { fetchDeliveryListCached, getDeliveryListCached } from '../../../../utils/cache/deliveryCache';
import { invoiceIssuanceService } from '../../../../api/services/invoiceIssuanceService';

const resolveCacheMaps = async (forceRefresh = false) => {
    let vendors = getVendorListCached() || [];
    let buyers = getBuyerListCached() || [];
    let managers = getManagerListCached() || [];
    let deliveries = getDeliveryListCached() || [];
    let items = getItemListCached() || [];

    const shouldFetch =
        forceRefresh ||
        [vendors, buyers, managers, deliveries, items].some((cache) => !cache || cache.length === 0);

    if (shouldFetch) {
        await Promise.all([
            fetchVendorListCached(forceRefresh),
            fetchBuyerListCached(forceRefresh),
            fetchManagerListCached(forceRefresh),
            fetchDeliveryListCached(forceRefresh),
            fetchItemListCached(forceRefresh)
        ]);
        vendors = getVendorListCached() || [];
        buyers = getBuyerListCached() || [];
        managers = getManagerListCached() || [];
        deliveries = getDeliveryListCached() || [];
        items = getItemListCached() || [];
    }

    return { vendors, buyers, managers, deliveries, items };
};

const extractListPayload = (data, requestOffset = 0) => {
    if (!data) return { items: [], totalCount: 0, hasMore: false };
    if (Array.isArray(data)) {
        return { items: data, totalCount: data.length, hasMore: false };
    }
    const items = Array.isArray(data.items)
        ? data.items
        : Array.isArray(data.data)
          ? data.data
          : [];
    const totalCount = Number.isFinite(Number(data.total_count))
        ? Number(data.total_count)
        : items.length;
    const nextOffset = requestOffset + items.length;
    const hasMore =
        typeof data.has_more === 'boolean'
            ? data.has_more
            : items.length > 0 && nextOffset < totalCount;
    return { items, totalCount, hasMore };
};

export const useInvoiceData = (cachedState) => {
    const [isLoading, setIsLoading] = useState(false);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [invoiceData, setInvoiceData] = useState(() => cachedState?.invoiceData ?? []);
    const [totalCount, setTotalCount] = useState(() => cachedState?.totalCount ?? 0);
    const [hasMore, setHasMore] = useState(() => cachedState?.hasMore ?? false);
    const [invoiceSummary, setInvoiceSummary] = useState(() => cachedState?.invoiceSummary ?? null);
    const [rawInvoiceData, setRawInvoiceData] = useState(() => cachedState?.rawInvoiceData ?? []);

    const fetchGenerationRef = useRef(0);
    const nextOffsetRef = useRef(0);
    const hasMoreRef = useRef(cachedState?.hasMore ?? false);
    const loadingMoreRef = useRef(false);
    const lastSearchParamsRef = useRef(null);
    const lastFetchOptionsRef = useRef({});
    const forceRefreshNextFetchRef = useRef(false);
    const pendingFetchRef = useRef(null);
    const pendingKeyRef = useRef(null);

    const markInvoiceSearchForceRefresh = useCallback(() => {
        forceRefreshNextFetchRef.current = true;
    }, []);

    const clearInvoiceIssuanceStorage = useCallback(() => {
        fetchGenerationRef.current += 1;
        nextOffsetRef.current = 0;
        hasMoreRef.current = false;
        loadingMoreRef.current = false;
        lastSearchParamsRef.current = null;
        lastFetchOptionsRef.current = {};
        pendingFetchRef.current = null;
        pendingKeyRef.current = null;
        setRawInvoiceData([]);
        setInvoiceData([]);
        setTotalCount(0);
        setHasMore(false);
        setInvoiceSummary(null);
        setIsLoading(false);
        setIsLoadingMore(false);
    }, []);

    const mapItemsWithCaches = useCallback(async (items, forceRefresh) => {
        const caches = await resolveCacheMaps(forceRefresh);
        return processInvoiceData(items, caches);
    }, []);

    const fetchPage = useCallback(
        async ({ searchParams, offset, append, generation, fetchOpts, forceRefresh }) => {
            const params = buildSearchParams(searchParams, {
                limit: ISSUANCE_PAGE_LIMIT,
                offset,
                createdBy: fetchOpts.showMyOrderOnly ? fetchOpts.userId || null : null,
                unissuedOnly: !!fetchOpts.hideInvoicedRows
            });

            const listRes = await invoiceIssuanceService.getIssuanceList(params);
            if (generation !== fetchGenerationRef.current) return;

            if (!listRes.success) {
                throw listRes.error || new Error('Failed to load invoice issuance list');
            }

            const { items, totalCount: serverTotal, hasMore: more } = extractListPayload(
                listRes.data,
                offset
            );
            const mapped = await mapItemsWithCaches(items, forceRefresh);
            if (generation !== fetchGenerationRef.current) return;

            nextOffsetRef.current = offset + items.length;
            hasMoreRef.current = more;
            setHasMore(more);
            setTotalCount(serverTotal);
            setRawInvoiceData((prev) => (append ? [...prev, ...mapped] : mapped));
            setInvoiceData((prev) => (append ? [...prev, ...mapped] : mapped));
        },
        [mapItemsWithCaches]
    );

    const fetchInvoiceData = useCallback(
        async (searchParams, setHasSearched, fetchOptions = {}) => {
            const fetchOpts = fetchOptions || {};
            const forceRefresh =
                fetchOpts.forceRefresh === true || forceRefreshNextFetchRef.current;
            forceRefreshNextFetchRef.current = false;

            const filterKey = JSON.stringify({
                search: buildSearchParams(searchParams, {
                    includePagination: false,
                    createdBy: fetchOpts.showMyOrderOnly ? fetchOpts.userId || null : null,
                    unissuedOnly: !!fetchOpts.hideInvoicedRows
                }),
                my: !!fetchOpts.showMyOrderOnly,
                unissued: !!fetchOpts.hideInvoicedRows,
                user: fetchOpts.userId ?? null
            });

            if (!forceRefresh && pendingFetchRef.current && pendingKeyRef.current === filterKey) {
                return pendingFetchRef.current;
            }

            const generation = ++fetchGenerationRef.current;
            loadingMoreRef.current = false;
            nextOffsetRef.current = 0;
            hasMoreRef.current = false;
            lastSearchParamsRef.current = searchParams;
            lastFetchOptionsRef.current = fetchOpts;

            const silent = fetchOpts.silent === true;
            if (!silent) {
                setIsLoading(true);
            }
            setIsLoadingMore(false);
            setHasMore(false);

            if (!silent) {
                setInvoiceData([]);
                setRawInvoiceData([]);
                setInvoiceSummary(null);
                setTotalCount(0);
            }

            const fetchPromise = (async () => {
                try {
                    const summaryParams = buildSummaryParams(searchParams, {
                        createdBy: fetchOpts.showMyOrderOnly ? fetchOpts.userId || null : null,
                        unissuedOnly: !!fetchOpts.hideInvoicedRows
                    });

                    const summaryPromise = invoiceIssuanceService
                        .getIssuanceListSummary(summaryParams)
                        .then((res) => {
                            if (generation !== fetchGenerationRef.current) return;
                            if (res.success) {
                                const uiSummary = mapIssuanceSummaryToUi(res.data);
                                setInvoiceSummary(uiSummary);
                                if (uiSummary && Number.isFinite(uiSummary.count)) {
                                    setTotalCount(uiSummary.count);
                                }
                            } else {
                                setInvoiceSummary(null);
                            }
                        })
                        .catch(() => {
                            if (generation !== fetchGenerationRef.current) return;
                            setInvoiceSummary(null);
                        });

                    await Promise.all([
                        fetchPage({
                            searchParams,
                            offset: 0,
                            append: false,
                            generation,
                            fetchOpts,
                            forceRefresh
                        }),
                        summaryPromise
                    ]);

                    if (generation === fetchGenerationRef.current) {
                        setHasSearched?.(true);
                    }
                } catch (error) {
                    if (generation !== fetchGenerationRef.current) return;
                    console.error('인보이스 발급 목록 조회 실패:', error);
                    setInvoiceData([]);
                    setRawInvoiceData([]);
                    setInvoiceSummary(null);
                    setTotalCount(0);
                    setHasMore(false);
                    hasMoreRef.current = false;
                    throw error;
                } finally {
                    if (generation === fetchGenerationRef.current) {
                        setIsLoading(false);
                    }
                    if (pendingFetchRef.current === fetchPromise) {
                        pendingFetchRef.current = null;
                        pendingKeyRef.current = null;
                    }
                }
            })();

            pendingFetchRef.current = fetchPromise;
            pendingKeyRef.current = filterKey;
            return fetchPromise;
        },
        [fetchPage]
    );

    const loadMore = useCallback(async () => {
        if (loadingMoreRef.current || isLoading || !hasMoreRef.current) return;
        const searchParams = lastSearchParamsRef.current;
        if (!searchParams) return;

        loadingMoreRef.current = true;
        setIsLoadingMore(true);
        const generation = fetchGenerationRef.current;
        const offset = nextOffsetRef.current;
        const fetchOpts = lastFetchOptionsRef.current || {};

        try {
            await fetchPage({
                searchParams,
                offset,
                append: true,
                generation,
                fetchOpts,
                forceRefresh: false
            });
        } catch (error) {
            console.error('인보이스 발급 목록 추가 로드 실패:', error);
        } finally {
            loadingMoreRef.current = false;
            if (generation === fetchGenerationRef.current) {
                setIsLoadingMore(false);
            }
        }
    }, [fetchPage, isLoading]);

    return {
        isLoading,
        isLoadingMore,
        isPackingLoading: false,
        isGiPostLoading: false,
        isInvoiceNoLoading: false,
        invoiceData,
        totalCount,
        hasMore,
        invoiceSummary,
        rawInvoiceData,
        fetchInvoiceData,
        loadMore,
        markInvoiceSearchForceRefresh,
        clearInvoiceIssuanceStorage,
        setInvoiceData,
        setTotalCount,
        setInvoiceSummary
    };
};
